"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Stepper = Stepper;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lucide_react_1 = require("lucide-react");
const cn_1 = require("../lib/cn");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A number with two buttons. Ours, not the browser's.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`<input type="number">` IS BANNED BY THE 9 Sep 2026 FEEDBACK** (`qol-fixes.md` item 66),
 * and three files in this repository had already worked around it for their own reasons —
 * `plans.tsx`, `sign-in.tsx` and `agent-proposal-card.tsx` each carry a note explaining what a
 * number input did to them. This component is those workarounds made reusable instead of repeated:
 *
 *  - **its spinners are drawn by the operating system** and take no CSS, which is item 23's
 *    complaint about the native dropdown in a smaller box;
 *  - **it reports `''` mid-typing** for `-`, for `1e` and for a lone `.`, so a controlled component
 *    reading `event.target.value` sees a field emptying itself under the person's hands;
 *  - **it strips leading zeros and silently rounds**, which is why `sign-in.tsx` refuses to use one
 *    for a six-digit code.
 *
 * ⚠️ **So the value is a STRING and stays one.** `TextInput` already works this way and the call
 * sites already hold strings; parsing belongs where the value is used, not in the control, because
 * a control that parses has to decide what `''` means and every caller disagrees.
 *
 * ⚠️ **`inputMode="decimal"`** rather than `numeric`: `numeric` hides the decimal point on a phone
 * keypad, and the daily maximum steps by 0.5.
 *
 * ⚠️ **The buttons clamp, the typing does not.** Somebody typing `999` into a field capped at 24
 * must be able to finish the word before being corrected — the form's own validation says so on
 * submit, which is where `state-rules.md` puts it. The buttons cannot overshoot because a click is
 * a complete action rather than a half-typed one.
 */
function Stepper({ value, onChange, min, max, step = 1, disabled = false, decrementLabel, incrementLabel, className, id, placeholder, ...rest }) {
    const fallbackId = (0, react_1.useId)();
    const inputId = id ?? fallbackId;
    /**
     * ⚠️ **A blank or half-typed value steps from the floor, not from `NaN`.** `Number('')` is 0 and
     * `Number('1e')` is `NaN`; adding a step to either produces a field somebody has to clear before
     * they can use the buttons at all.
     */
    const nudge = (delta) => {
        const parsed = Number.parseFloat(value);
        const from = Number.isFinite(parsed) ? parsed : (min ?? 0);
        let next = from + delta * step;
        if (min !== undefined)
            next = Math.max(min, next);
        if (max !== undefined)
            next = Math.min(max, next);
        /* ⚠️ Rounded to the step's own precision: 0.1 + 0.2 is 0.30000000000000004, and a stepper that
           produces that has failed at the one thing it does. */
        const places = String(step).split('.')[1]?.length ?? 0;
        onChange(next.toFixed(places));
    };
    const atMin = min !== undefined && Number.parseFloat(value) <= min;
    const atMax = max !== undefined && Number.parseFloat(value) >= max;
    return ((0, jsx_runtime_1.jsxs)("div", { className: (0, cn_1.cn)('border-input focus-within:border-ring focus-within:ring-ring/50 inline-flex h-8 items-center rounded-md border shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]', disabled ? 'opacity-50' : '', className), children: [(0, jsx_runtime_1.jsx)("button", { type: "button", tabIndex: -1, "aria-label": decrementLabel, disabled: disabled || atMin, onClick: () => {
                    nudge(-1);
                }, className: "text-muted-foreground hover:text-foreground flex h-full w-7 shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Minus, { size: 13, "aria-hidden": "true" }) }), (0, jsx_runtime_1.jsx)("input", { id: inputId, type: "text", inputMode: "decimal", disabled: disabled, value: value, ...(placeholder === undefined ? {} : { placeholder }), onChange: (event) => {
                    onChange(event.target.value);
                }, onKeyDown: (event) => {
                    /* ⚠️ The arrows a native number input gave us, kept — they are the reason people reach
                       for one, and losing them would be a regression dressed as a fix. */
                    if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        nudge(1);
                    }
                    else if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        nudge(-1);
                    }
                }, className: "placeholder:text-muted-foreground w-full min-w-0 bg-transparent px-1 text-center text-sm tabular-nums outline-none disabled:cursor-not-allowed", ...rest }), (0, jsx_runtime_1.jsx)("button", { type: "button", tabIndex: -1, "aria-label": incrementLabel, disabled: disabled || atMax, onClick: () => {
                    nudge(1);
                }, className: "text-muted-foreground hover:text-foreground flex h-full w-7 shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Plus, { size: 13, "aria-hidden": "true" }) })] }));
}
//# sourceMappingURL=stepper.js.map